import { useEffect, useState } from "react";
import posthog from "posthog-js";
import KickClient from "~/visits/utils/api.js";
import { getUserID, getVisitID } from "~/visits/utils/visitCookies.js";
import { useExperimentVariant } from "../useExperimentVariant";

import { EXPERIMENT_CHECKOUT_DELIVERY_DATES } from "../experimentKeys";
export const DELIVERY_DATES_EXPERIMENT = EXPERIMENT_CHECKOUT_DELIVERY_DATES;
const decisions = new Map();

export function trackDeliveryExperiment(checkoutSessionID, event, code = "") {
  if (!checkoutSessionID) return Promise.resolve();
  const decision = decisions.get(`${getUserID()}:${checkoutSessionID}`);
  if (event === "Checkout Delivery Experiment Viewed" && !decision?.enrolled)
    return Promise.resolve();
  const visitID = decision?.visitID || getVisitID(checkoutSessionID);
  if (!visitID) return Promise.resolve();
  return KickClient.newAuthenticatedClient()
    .post(
      `/visits/${visitID}/delivery-experiment`,
      {
        checkout_session_id: checkoutSessionID,
        variant: decision?.test ? "test" : "control",
        event,
        code,
      },
      { timeout: 2000 },
    )
    .catch(() => {});
}

// Assignment is frozen for this checkout. A late flag response cannot switch the
// experience after the patient has started editing their address.
export function useDeliveryDatesExperiment({
  eligible,
  checkoutSessionID,
  visitID,
  orderType,
  consultationType,
  isRefill,
}) {
  const userID = getUserID();
  const key = `${userID}:${checkoutSessionID}`;
  useEffect(() => {
    if (eligible && userID) posthog.identify(userID);
  }, [eligible, userID]);
  const { ready: flagsReady } = useExperimentVariant(
    DELIVERY_DATES_EXPERIMENT,
    {
      eligible: eligible && Boolean(userID),
    },
  );
  const [decision, setDecision] = useState(() => decisions.get(key));

  useEffect(() => {
    if (!eligible || !userID) {
      setDecision({ test: false, enrolled: false, key });
      return;
    }
    if (decisions.has(key)) {
      setDecision(decisions.get(key));
      return;
    }
    let canceled = false;
    const settle = (result) => {
      if (canceled || decisions.has(key)) return;
      const keyed = { ...result, key };
      decisions.set(key, keyed);
      setDecision(keyed);
    };
    const timer = setTimeout(
      () => settle({ test: false, enrolled: false }),
      2500,
    );
    if (flagsReady) {
      const variant = posthog.getFeatureFlag(DELIVERY_DATES_EXPERIMENT, {
        send_event: false,
      });
      if (variant !== "test" && variant !== "control") {
        settle({ test: false, enrolled: false });
      } else {
        // The server atomically retains the first enrollment across tabs/devices.
        KickClient.newAuthenticatedClient()
          .post(
            `/visits/${visitID}/delivery-experiment`,
            {
              variant,
              checkout_session_id: checkoutSessionID,
              order_type: orderType,
              consultation_type: consultationType,
              device_type: window.innerWidth < 960 ? "mobile" : "desktop",
              is_refill: isRefill,
            },
            { timeout: 2000 },
          )
          .then(({ data }) => {
            settle({
              test: data.variant === "test",
              enrolled: data.checkout_session_id === checkoutSessionID,
              visitID,
            });
          })
          .catch(() => settle({ test: false, enrolled: false }));
      }
    }
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [
    eligible,
    userID,
    key,
    flagsReady,
    checkoutSessionID,
    visitID,
    orderType,
    consultationType,
    isRefill,
  ]);

  return {
    ready: decision?.key === key,
    enabled: Boolean(decision?.key === key && decision?.test),
  };
}

function readSelectedEstimate(checkoutSessionID) {
  try {
    return (
      sessionStorage.getItem(
        `delivery-estimate:${getUserID()}:${checkoutSessionID}`,
      ) || ""
    );
  } catch {
    return "";
  }
}

export function deliveryExperimentCheckoutFields(checkoutSessionID) {
  const decision = decisions.get(`${getUserID()}:${checkoutSessionID}`);
  return {
    checkoutSessionID,
    deliveryEstimateID:
      decision?.estimateID || readSelectedEstimate(checkoutSessionID) || "",
  };
}

export function rememberDeliveryEstimate(checkoutSessionID, estimateID) {
  const decision = decisions.get(`${getUserID()}:${checkoutSessionID}`);
  if (decision) decision.estimateID = estimateID;
  try {
    sessionStorage.setItem(
      `delivery-estimate:${getUserID()}:${checkoutSessionID}`,
      estimateID || "",
    );
  } catch {
    /* Analytics storage must not block checkout. */
  }
}

// Payment is a separate route and can be reloaded, so its server event is keyed
// by the durable enrollment rather than relying on browser memory.
export function trackDeliveryPaymentStep(checkoutSessionID) {
  if (!checkoutSessionID) return;
  const visitID = getVisitID(checkoutSessionID);
  if (!visitID) return;
  KickClient.newAuthenticatedClient()
    .post(
      `/visits/${visitID}/delivery-experiment`,
      {
        checkout_session_id: checkoutSessionID,
        event: "Checkout Delivery Payment Step",
      },
      { timeout: 2000 },
    )
    .catch(() => {});
}
